import React, { useState } from "react";
import { shallowEqual } from "react-redux";

import { useAppSelector } from "../../../app/store";

import {
  EntityType,
  RelatedAs,
} from "../../../api/stakeholder_relationships";

import {
  getStakeholderById,
  getStakeholderRelationshipsByEntity,
} from "../logic/stakeholderSlice";

import StakeholderItem from "./StakeholderItem";
import AddStakeholderButton from "./AddStakeholderButton";
import AddStakeholderModal from "./AddStakeholderModal";

const BORROWER_RELATIONSHIPS = [
  RelatedAs.PRIMARY_BORROWER,
  RelatedAs.CO_BORROWER,
  RelatedAs.BORROWER,
];

export interface BorrowersSectionProps {
  accountNumber: string;
  onBorrowerClick: (stakeholderId: string) => void;
}

const BorrowersSection: React.FC<BorrowersSectionProps> = ({
  accountNumber,
  onBorrowerClick,
}) => {
  const [showAddBorrowerModal, setShowAddBorrowerModal] =
    useState(false);

  const [addBorrowerRelatedAs, setAddBorrowerRelatedAs] =
    useState<RelatedAs | null>(null);

  const relationships = useAppSelector((state) =>
    getStakeholderRelationshipsByEntity(
      state,
      EntityType.LOAN_ACCOUNT,
      accountNumber,
      BORROWER_RELATIONSHIPS,
    ),
  );

  const primaryBorrowerRelationship = relationships.find(
    (relationship) =>
      relationship.related_as === RelatedAs.PRIMARY_BORROWER,
  );

  const coBorrowerRelationships = relationships.filter(
    (relationship) =>
      relationship.related_as === RelatedAs.CO_BORROWER,
  );

  const borrowerRelationships = relationships.filter(
    (relationship) =>
      relationship.related_as === RelatedAs.BORROWER,
  );

  const primaryBorrower = useAppSelector((state) =>
    primaryBorrowerRelationship
      ? getStakeholderById(
          state,
          primaryBorrowerRelationship.stakeholder_id,
        )
      : undefined,
  );

  const coBorrowers = useAppSelector(
    (state) =>
      coBorrowerRelationships
        .map((relationship) =>
          getStakeholderById(
            state,
            relationship.stakeholder_id,
          ),
        )
        .filter(Boolean),
    shallowEqual,
  );

  const borrowers = useAppSelector(
    (state) =>
      borrowerRelationships
        .map((relationship) =>
          getStakeholderById(
            state,
            relationship.stakeholder_id,
          ),
        )
        .filter(Boolean),
    shallowEqual,
  );

  const handleAddPrimaryBorrower = () => {
    setAddBorrowerRelatedAs(RelatedAs.PRIMARY_BORROWER);
    setShowAddBorrowerModal(true);
  };

  const handleAddCoBorrower = () => {
    setAddBorrowerRelatedAs(RelatedAs.CO_BORROWER);
    setShowAddBorrowerModal(true);
  };

  const handleCloseAddBorrowerModal = () => {
    setShowAddBorrowerModal(false);
    setAddBorrowerRelatedAs(null);
  };

  return (
    <>
      <div className="flex w-full  flex-col gap-3">
        <div className="flex items-center">
          <span className="text-sm font-medium leading-5 text-[#0A0A0A]">
            Borrowers
          </span>
        </div>

        <div className="flex w-full flex-wrap items-center gap-2">
          {primaryBorrower && (
            <div className="w-[278px] shrink-0">
              <StakeholderItem
                name={primaryBorrower.name}
                relatedAs={RelatedAs.PRIMARY_BORROWER}
                onClick={() =>
                  onBorrowerClick(
                    primaryBorrower.stakeholder_id,
                  )
                }
                className="w-full"
              />
            </div>
          )}

          {coBorrowers.map((coBorrower) => {
            if (!coBorrower) {
              return null;
            }

            return (
              <div
                key={coBorrower.stakeholder_id}
                className="w-[278px] shrink-0"
              >
                <StakeholderItem
                  name={coBorrower.name}
                  relatedAs={RelatedAs.CO_BORROWER}
                  onClick={() =>
                    onBorrowerClick(
                      coBorrower.stakeholder_id,
                    )
                  }
                  className="w-full"
                />
              </div>
            );
          })}

          {borrowers.map((borrower) => {
            if (!borrower) {
              return null;
            }

            return (
              <div
                key={borrower.stakeholder_id}
                className="w-[278px] shrink-0"
              >
                <StakeholderItem
                  name={borrower.name}
                  relatedAs={RelatedAs.BORROWER}
                  onClick={() =>
                    onBorrowerClick(
                      borrower.stakeholder_id,
                    )
                  }
                  className="w-full"
                />
              </div>
            );
          })}

          {!primaryBorrower && (
            <AddStakeholderButton
              label="Add Primary Borrower"
              onClick={handleAddPrimaryBorrower}
            />
          )}

          {!coBorrowers.length && (
            <AddStakeholderButton
              label="Add Co-Borrower"
              onClick={handleAddCoBorrower}
            />
          )}

          {coBorrowers.length > 0 && (
            <AddStakeholderButton
              label="Add"
              onClick={handleAddCoBorrower}
            />
          )}
        </div>
      </div>

      {showAddBorrowerModal && addBorrowerRelatedAs && (
        <AddStakeholderModal
          accountNumber={accountNumber}
          relatedAs={addBorrowerRelatedAs}
          onClose={handleCloseAddBorrowerModal}
        />
      )}
    </>
  );
};

export default BorrowersSection;